import React from 'react'
import styled from '@emotion/styled'
import { gql } from '@apollo/client'
import dynamic from 'next/dynamic'
import { Cover, device, space, color } from '@ticketswap/solar'
import { withTranslation } from '~/i18n'
import SiteSearch from '~/components/SiteSearch'
import Menubar from '~/components/Menubar'
import { MenubarContainer } from '~/components/Menubar'
import { NextSeo } from 'next-seo'
import SiteFooter from '~/components/SiteFooter'
import HomePageContent from '~/components/HomePageContent'
import FeedbackWidget from '~/components/FeedbackWidget'
import Testimonials from '~/components/Testimonials'
import HomepageStructuredData from '~/components/HomepageStructuredData'
import { PageTitle } from '~/components/PageTitle'
import { NarrowPageLayout, WidePageLayout } from '~/components/PageLayout'
import TrustFactors from '~/components/TrustFactors'
import AppsPayoff from '~/components/AppsPayoff'
import { useViewer } from '~/components/ViewerContext'

const HomepageAnnouncement = dynamic(() => import('~/components/HomepageAnnouncement'), {
  ssr: false,
  loading: () => null,
})

const StyledCover = styled(Cover)`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  height: 75vh;
  max-height: 600px;
  min-height: 500px;
`

const StyledPageTitle = styled(PageTitle)`
  padding: 0 ${space[16]};
  margin: ${space[16]} 0;

  h1 {
    color: ${color.nova};

    [data-theme='dark'] & {
      color: ${color.space};
    }
  }

  @media ${device.tablet} {
    text-align: center;
    margin: ${space[32]} 0;
  }
`

const BackgroundContainer = styled.div`
  position: relative;
  z-index: 0;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 1) 300px,
    rgba(251, 251, 252, 1) 400px,
    rgba(251, 251, 252, 1) 90%,
    rgba(255, 255, 255, 1) 100%
  );

  [data-theme='dark'] & {
    background: ${color.nova};
  }
`

const Home = ({ t, hostname, cover }) => {
  const { viewer } = useViewer()
  const userHasDeviceConnected = viewer?.userDevices?.length > 0

  return (
    <>
      <NextSeo
        title={`${t('site.name')}: ${t('site.title')}`}
        openGraph={{
          title: `${t('site.name')}: ${t('site.title')}`,
          site_name: t('site.name'),
        }}
      />
      <StyledCover
        caption={cover.copyrightText}
        captionUrl={cover.copyrightLink}
        imageUrl={cover.imageSizes.desktop}
        images={cover.imageSizes}
        data-hide-feedback-widget="true"
      >
        <MenubarContainer>
          <Menubar hasSearchToggle={false} appearance="light" />
        </MenubarContainer>
        <StyledPageTitle primary={t('home:tagline')} />
        <NarrowPageLayout>
          <SiteSearch />
          <HomepageAnnouncement />
          <TrustFactors t={t} />
        </NarrowPageLayout>
      </StyledCover>
      <BackgroundContainer>
        <HomePageContent t={t} />
        <Testimonials />
      </BackgroundContainer>
      {!userHasDeviceConnected && (
        <WidePageLayout>
          <AppsPayoff t={t} />
        </WidePageLayout>
      )}
      <FeedbackWidget />
      <SiteFooter />
      <HomepageStructuredData hostname={hostname} />
    </>
  )
}

const GET_COVER = gql`
  query getRandomCover {
    randomBackgroundImage {
      id
      copyrightText
      copyrightLink
      imageSizes {
        desktop
        tablet
        mobileLandscape
        mobilePortrait
      }
    }
  }
`

Home.getInitialProps = async ({ req, apolloClient }) => {
  const { data } = await apolloClient.query({
    query: GET_COVER,
  })

  return {
    hostname: req ? req.hostname : window.location.hostname,
    namespacesRequired: ['common', 'home'],
    cover: data.randomBackgroundImage,
  }
}

export default withTranslation(['common', 'home'])(Home)
